import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Mail, Building, User, Calendar, MessageSquare, DollarSign, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [tab, setTab] = useState('contacts');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContacts();
    fetchPublishers();
  }, []);

  const fetchPublishers = async () => {
    try {
      const response = await axios.get(`${API}/publisher-applications`);
      if (response.data.success) {
        setPublishers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching publisher applications:', err);
    }
  };

  const siteTypeLabels = {
    blog: 'Blog / média',
    coupon: 'Coupons & deals',
    newsletter: 'Newsletter',
    comparison: 'Comparateur',
    cashback: 'Cashback / fidélité',
    social: 'Réseaux sociaux',
    extension: 'Extension navigateur',
    other: 'Autre'
  };

  const trafficLabels = {
    'under-50k': '< 50k visites/mois',
    '50k-250k': '50k - 250k visites/mois',
    '250k-1m': '250k - 1M visites/mois',
    '1m-5m': '1M - 5M visites/mois',
    'over-5m': '> 5M visites/mois'
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact-requests`);
      if (response.data.success) {
        setContacts(response.data.data);
      } else {
        setError('Erreur lors du chargement des contacts');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Erreur de connexion à l\'API');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBudgetLabel = (budget) => {
    const budgetLabels = {
      'under-10k': 'Moins de 10 000€',
      '10k-50k': '10 000€ - 50 000€',
      '50k-100k': '50 000€ - 100 000€',
      '100k-500k': '100 000€ - 500 000€',
      'over-500k': 'Plus de 500 000€'
    };
    return budgetLabels[budget] || budget;
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'qualified': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors['new'];
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'Nouveau',
      'contacted': 'Contacté',
      'qualified': 'Qualifié',
      'closed': 'Fermé'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des contacts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                onClick={() => navigate('/')}
                variant="ghost" 
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au site
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
                <p className="text-gray-600">Gestion des demandes de contact OptimHub</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_traffic-partner/artifacts/hrx2swuq_logo2%20%281%29.png" 
                alt="OptimHub" 
                className="h-8 w-auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <Button 
              onClick={fetchContacts}
              variant="outline"
              size="sm"
              className="mt-2"
            >
              Réessayer
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8">
          <Button
            data-testid="admin-tab-contacts"
            onClick={() => setTab('contacts')}
            variant={tab === 'contacts' ? 'default' : 'outline'}
            size="sm"
          >
            Demandes de contact ({contacts.length})
          </Button>
          <Button
            data-testid="admin-tab-publishers"
            onClick={() => setTab('publishers')}
            variant={tab === 'publishers' ? 'default' : 'outline'}
            size="sm"
          >
            Candidatures éditeurs ({publishers.length})
          </Button>
        </div>

        {tab === 'contacts' && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Mail className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <User className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Nouveaux</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {contacts.filter(c => c.status === 'new').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Building className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Qualifiés</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {contacts.filter(c => c.status === 'qualified').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <DollarSign className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Budget élevé</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {contacts.filter(c => c.budget === 'over-500k' || c.budget === '100k-500k').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contacts Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Demandes de contact ({contacts.length})</span>
              <Button onClick={fetchContacts} variant="outline" size="sm">
                Actualiser
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucune demande de contact pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {contact.firstName[0]}{contact.lastName[0]}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {contact.firstName} {contact.lastName}
                          </h3>
                          <p className="text-gray-600">{contact.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Building className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{contact.company}</span>
                            {contact.role && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-sm text-gray-600">{contact.role}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge className={getStatusColor(contact.status)}>
                          {getStatusLabel(contact.status)}
                        </Badge>
                        <Badge variant="outline">
                          {contact.language?.toUpperCase() || 'FR'}
                        </Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          Budget: {getBudgetLabel(contact.budget)}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {formatDate(contact.createdAt)}
                        </span>
                      </div>
                    </div>

                    {contact.message && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-700">{contact.message}</p>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                      <span>IP: {contact.ipAddress}</span>
                      <span>ID: {contact.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
        )}

        {tab === 'publishers' && (
          <Card data-testid="admin-publishers-panel">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Candidatures éditeurs ({publishers.length})</span>
                <Button onClick={fetchPublishers} variant="outline" size="sm">
                  Actualiser
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {publishers.length === 0 ? (
                <div className="text-center py-8">
                  <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucune candidature éditeur pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {publishers.map((publisher) => (
                    <div
                      key={publisher.id}
                      data-testid={`admin-publisher-${publisher.id}`}
                      className="border rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{publisher.siteName}</h3>
                          <a
                            href={publisher.siteUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            {publisher.siteUrl}
                          </a>
                          <p className="text-gray-600 text-sm mt-1">
                            {publisher.name} • {publisher.email}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge className={getStatusColor(publisher.status)}>
                            {publisher.status}
                          </Badge>
                          <Badge variant="outline">{publisher.language?.toUpperCase() || 'FR'}</Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <span>Type : {siteTypeLabels[publisher.siteType] || publisher.siteType}</span>
                        <span>Trafic : {trafficLabels[publisher.monthlyTraffic] || publisher.monthlyTraffic}</span>
                        <span>Zones : {publisher.countries}</span>
                        <span>Verticales : {publisher.verticals}</span>
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(publisher.createdAt)}
                        </span>
                      </div>

                      {publisher.message && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-start space-x-2">
                          <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-700">{publisher.message}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;